import { useMutation } from '@apollo/client';
import {
  CreateMatterDocument,
  UpdateMatterDocument,
  CloseMatterDocument,
  AddExpertToMatterDocument,
  UpdateExpertParticipationDocument,
  RemoveExpertFromMatterDocument,
} from '~/graphql/mutations/matterMutations';
import {
  MattersDocument,
  MatterDetailDocument,
} from '~/graphql/queries/matters';
import { MyMatterExpertsDocument } from '~/graphql/queries/myMatterExperts';
import type {
  CreateMatterInput,
  UpdateMatterInput,
  ExpertParticipationDecision,
  MatterExpertExitReason,
} from '~/gql/graphql';

function refetchMattersMatterDetailAndMyExperts(matterId: string) {
  return [
    MattersDocument,
    { query: MatterDetailDocument, variables: { id: matterId } },
    MyMatterExpertsDocument,
  ];
}

export function useMatterMutations() {
  const [createMatterMutation, { loading: isCreating }] =
    useMutation(CreateMatterDocument);

  const [updateMatterMutation, { loading: isUpdating }] =
    useMutation(UpdateMatterDocument);

  const [closeMatterMutation, { loading: isClosing }] =
    useMutation(CloseMatterDocument);

  const [addExpertMutation, { loading: isAddingExpert }] = useMutation(
    AddExpertToMatterDocument,
  );

  const [
    updateExpertParticipationMutation,
    { loading: isUpdatingExpertParticipation },
  ] = useMutation(UpdateExpertParticipationDocument);

  const [removeExpertMutation, { loading: isRemovingExpert }] = useMutation(
    RemoveExpertFromMatterDocument,
  );

  const createMatter = async (input: CreateMatterInput) => {
    const { data } = await createMatterMutation({
      variables: { input },
      refetchQueries: [MattersDocument, MyMatterExpertsDocument],
      // Wait for the matters list refetch so that when the caller navigates to
      // the new matter detail page, the matters cache already includes it and
      // we don't briefly render a "Matter not found" empty state.
      awaitRefetchQueries: true,
    });
    return data?.createMatter ?? null;
  };

  const updateMatter = async (id: string, input: UpdateMatterInput) => {
    const { data } = await updateMatterMutation({
      variables: { id, input },
    });
    return data?.updateMatter ?? null;
  };

  const closeMatter = async (id: string) => {
    const { data } = await closeMatterMutation({
      variables: { id },
    });
    return data?.closeMatter ?? null;
  };

  const addExpertToMatter = async (matterId: string, expertId: string) => {
    const { data } = await addExpertMutation({
      variables: { matterId, expertId },
      refetchQueries: refetchMattersMatterDetailAndMyExperts(matterId),
    });
    return data?.addExpertToMatter ?? null;
  };

  const updateExpertParticipation = async (params: {
    matterId: string;
    expertId: string;
    status: ExpertParticipationDecision;
    exitReason?: MatterExpertExitReason;
    exitComment?: string;
  }) => {
    const { data } = await updateExpertParticipationMutation({
      variables: params,
      refetchQueries: refetchMattersMatterDetailAndMyExperts(params.matterId),
    });
    return data?.updateExpertParticipation ?? null;
  };

  const removeExpertFromMatter = async (params: {
    matterId: string;
    expertId: string;
    exitReason?: MatterExpertExitReason;
    exitComment?: string;
  }) => {
    const { data } = await removeExpertMutation({
      variables: params,
      refetchQueries: refetchMattersMatterDetailAndMyExperts(params.matterId),
    });
    return data?.removeExpertFromMatter ?? null;
  };

  return {
    createMatter,
    updateMatter,
    closeMatter,
    addExpertToMatter,
    updateExpertParticipation,
    removeExpertFromMatter,
    isCreating,
    isUpdating,
    isClosing,
    isAddingExpert,
    isUpdatingExpertParticipation,
    isRemovingExpert,
  };
}
